import { useCallback, useEffect, useMemo, useState } from 'react';

import { CustomEventNames } from 'features/analytics';
import { logNavigationClick } from 'features/navigation/utils/analytics';

import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  OrderLocatorTab,
  OrderLocatorTabVariations,
  defaultOrderLocatorTabVariation,
} from 'state/launchdarkly/variations';

import { IStoreLocatorTabsProps, IStoreLocatorTabsReturn, LocatorTabIds } from '../types';

/**
 * @name orderTabsForUnauthenticatedUser
 * @description we always want to show the NEARBY tab first
 *  for unauthed users while still preserving the custom order
 *  and visibility based on LD.
 */
const orderTabsForUnauthenticatedUser = (ldTabsOrder: OrderLocatorTab[]) => [
  OrderLocatorTab.NEARBY,
  ...ldTabsOrder.filter(tab => tab !== OrderLocatorTab.NEARBY),
];

const getTabIndex = (tab: OrderLocatorTab, locatorTabsEnabledAndOrdered: OrderLocatorTab[]) => {
  return locatorTabsEnabledAndOrdered.indexOf(tab);
};

export default function useStoreLocatorTabs({
  errorNearby,
  errorFavs,
  errorRecent,
}: IStoreLocatorTabsProps): IStoreLocatorTabsReturn {
  const { isAuthenticated } = useAuthContext();
  const locatorTabsOrder =
    useFlag<OrderLocatorTabVariations>(LaunchDarklyFlag.ORDER_LOCATOR_TABS) ||
    defaultOrderLocatorTabVariation;
  const [currentTabIndex, setTabIndex] = useState(0);
  const [currentTabKey, setTabKey] = useState<OrderLocatorTab>(OrderLocatorTab.NEARBY);
  // Track whether or not the current tab is in an error state
  const [currentTabHasError, setCurrentTabHasError] = useState(false);

  // TODO: potentially also track tab empty state status here?
  const ldTabData: OrderLocatorTab[] = useMemo(() => Object.values(locatorTabsOrder), [
    locatorTabsOrder,
  ]);

  const locatorTabsEnabledAndOrdered = isAuthenticated
    ? ldTabData
    : orderTabsForUnauthenticatedUser(ldTabData);

  /**
   * @name locatorTabIds
   *  maps all possible tabs with their index in the final order
   *  any non-visible tabs have a value of -1, but are necessary
   *  to have in the map for type safety and ease of use by consumers
   */
  const locatorTabIds = Object.values(OrderLocatorTab).reduce((acc, cur: OrderLocatorTab) => {
    const tabIdx = locatorTabsEnabledAndOrdered.indexOf(cur);
    acc[cur] = tabIdx;
    return acc;
  }, {} as LocatorTabIds);

  const currentTab = locatorTabsEnabledAndOrdered[currentTabIndex];

  const handleTabChange = useCallback(
    (tabKey: OrderLocatorTab) => {
      const index = getTabIndex(tabKey, locatorTabsEnabledAndOrdered);
      const newTabName = locatorTabsEnabledAndOrdered[index];
      logNavigationClick(CustomEventNames.BUTTON_CLICK_STORE_LOCATOR_TAB, {
        Tab: newTabName,
      });

      setTabIndex(index);
      setTabKey(tabKey);
    },
    [locatorTabsEnabledAndOrdered]
  );

  // update whether or not the current tab has an error
  // check any time the tab itself or any of the tab error states changes
  useEffect(() => {
    let tabHasErr = false;
    switch (currentTab) {
      case OrderLocatorTab.NEARBY:
        if (errorNearby) {
          tabHasErr = true;
        }
        break;
      case OrderLocatorTab.FAVORITE:
        if (errorFavs) {
          tabHasErr = true;
        }
        break;
      case OrderLocatorTab.RECENT:
        if (errorRecent) {
          tabHasErr = true;
        }
        break;
      default:
    }
    // TODO: should we only update if value has actually changed?
    setCurrentTabHasError(tabHasErr);
  }, [currentTab, errorFavs, errorNearby, errorRecent]);

  return {
    currentTab,
    currentTabHasError,
    currentTabIndex,
    currentTabKey,
    handleTabChange,
    locatorTabIds,
    locatorTabs: locatorTabsEnabledAndOrdered,
  };
}
